import { AsyncPipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  EventEmitter,
  Input,
  input,
  Output,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import type {
  ContentDetailsParams,
  SearchResultItem,
} from '@bizzdesign/data-access-search';
import { SharedI18nModule } from '@bizzdesign/shared/i18n';
import type { BDMenuItem } from '@bizzdesign/shared/models';
import {
  baseRoutes,
  buildSearchResultObjectMenuOptions,
  copyValueToClipboard,
} from '@bizzdesign/shared/utils';
import {
  ApplyMenuItemPermissionsPipe,
  TranslateMenuItemsPipe,
} from '@bizzdesign/shared/pipes';
import { BdMenuItemComponent } from '@bizzdesign/ui-menu';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { type TieredMenu, TieredMenuModule } from 'primeng/tieredmenu';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'bd-object-search-result-item',
  imports: [
    AsyncPipe,
    NgClass,
    FormsModule,
    SharedI18nModule,
    CheckboxModule,
    ButtonModule,
    TooltipModule,
    TieredMenuModule,
    BdMenuItemComponent,
    ApplyMenuItemPermissionsPipe,
    TranslateMenuItemsPipe,
    RouterModule,
  ],
  templateUrl: './object-search-result-item.component.html',
  styleUrl: './object-search-result-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ObjectSearchResultItemComponent {
  private readonly router = inject(Router);
  private _item: SearchResultItem | undefined;

  @Input({ required: true }) set item(value: SearchResultItem) {
    this._item = value;
    this.setActionMenuItems();
  }
  get item(): SearchResultItem {
    return this._item as SearchResultItem;
  }

  @Input() isSelectionMode: boolean = false;
  @Input() selectedItems: SearchResultItem[] = [];
  @Input() isActive: boolean = false;
  manageWorkspaceActionDisabled = input(false);
  manageWorkspaceNoAccessTooltip = input('');

  @Output() openContentDetails: EventEmitter<ContentDetailsParams> =
    new EventEmitter();
  @Output() openSearchResultItem: EventEmitter<SearchResultItem> =
    new EventEmitter();
  @Output() deleteSearchResultItem: EventEmitter<SearchResultItem> =
    new EventEmitter();
  @Output() selectedItemsChange = new EventEmitter<SearchResultItem[]>();
  @Output() itemSelectionToggled: EventEmitter<{
    shiftKey: boolean;
    itemId: SearchResultItem['id'];
  }> = new EventEmitter();

  actionMenuItems: BDMenuItem[] = [];
  doubleClick: boolean = false;
  doubleClickTimer: ReturnType<typeof setTimeout> | undefined = undefined;

  constructor() {
    effect(() => {
      this.manageWorkspaceActionDisabled();
      this.manageWorkspaceNoAccessTooltip();
      this.setActionMenuItems();
    });
  }

  get actionDisabled(): boolean {
    return this.item.deleted || this.manageWorkspaceActionDisabled();
  }

  get actionRoleTooltip(): string {
    return this.actionDisabled ? this.manageWorkspaceNoAccessTooltip() : '';
  }

  get isRowFocusable(): boolean {
    return !this.item.deleted && !this.item.disabled;
  }

  get itemTooltipKey():
    | 'deletedItemTooltip'
    | 'itemDisabledTooltip'
    | undefined {
    if (this.item.deleted) {
      return 'deletedItemTooltip';
    }

    if (this.item.disabled && this.isSelectionMode) {
      return 'itemDisabledTooltip';
    }

    return undefined;
  }

  private setActionMenuItems(): void {
    if (!this._item) {
      this.actionMenuItems = [];
      return;
    }
    this.actionMenuItems = buildSearchResultObjectMenuOptions(
      {
        onCopyLink: () => this.onCopyLinkClick(),
        onAddTo: () => this.onOpenClick(),
        onDelete: () => this.onDeleteClick(),
        onSeeDetails: () => this.openContentDetailsDrawer(),
      },
      {
        disabled: this.actionDisabled,
        disabledTooltip: this.manageWorkspaceNoAccessTooltip(),
        includeDelete: this.item.externalId === undefined,
      },
    );
  }

  onSelectedItemsChange(newValue: SearchResultItem[]): void {
    this.selectedItemsChange.emit(newValue);
  }

  onRowClick(event: MouseEvent): void {
    if (
      this.isContentDetailsInteractionBlocked() ||
      this.isFromNestedInteractiveTarget(event)
    ) {
      return;
    }
    this.onItemClick();
  }

  onRowDoubleClick(event: MouseEvent): void {
    if (
      this.isRowInteractionBlocked() ||
      this.isFromNestedInteractiveTarget(event)
    ) {
      return;
    }
    this.onItemDoubleClick();
    event.stopPropagation();
  }

  onRowEnter(event: KeyboardEvent): void {
    if (
      this.isRowInteractionBlocked() ||
      this.isFromNestedInteractiveTarget(event)
    ) {
      return;
    }
    this.onItemDoubleClick();
    event.preventDefault();
  }

  onRowKeyDown(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    if (keyboardEvent.key !== 'Enter') {
      return;
    }
    this.onRowEnter(keyboardEvent);
  }

  onItemDoubleClick(): void {
    this.doubleClick = true;
    clearTimeout(this.doubleClickTimer);
    this.openSearchResultItem.emit(this.item);
  }

  onCheckboxClick(event: Event, itemId: SearchResultItem['id']): void {
    event.stopPropagation();
    this.itemSelectionToggled.emit({
      shiftKey: !!(event as PointerEvent | KeyboardEvent).shiftKey,
      itemId,
    });
  }

  onCheckboxKeyDown(event: Event, itemId: SearchResultItem['id']): void {
    const keyboardEvent = event as KeyboardEvent;
    if (keyboardEvent.key !== 'Enter' && keyboardEvent.key !== ' ') {
      return;
    }
    this.onCheckboxClick(event, itemId);
  }

  openContentDetailsDrawer(scrollToUsedIn: boolean = false): void {
    this.openContentDetails.emit({ item: this.item, scrollToUsedIn });
  }

  onItemClick(): void {
    this.doubleClick = false;
    const delay = 250;
    this.doubleClickTimer = setTimeout(() => {
      if (!this.doubleClick) {
        this.openContentDetailsDrawer();
      }
    }, delay);
  }

  onOpenClick(): void {
    this.openSearchResultItem.emit(this.item);
  }

  onCopyLinkClick(): void {
    const urlTree = this.router.createUrlTree([baseRoutes.home, 'search'], {
      queryParams: {
        version: '1.0.0',
        drawerObjectId: JSON.stringify(this.item.id),
      },
    });
    copyValueToClipboard(
      new URL(
        this.router.serializeUrl(urlTree),
        window.location.origin,
      ).toString(),
    );
  }

  onActionButtonClick(event: Event): void {
    event.stopPropagation();
    if (this.actionDisabled) {
      return;
    }
    this.onOpenClick();
  }

  onMoreViewsClick(event: Event): void {
    this.openContentDetailsDrawer(true);
    event.stopPropagation();
  }

  onDeleteClick(): void {
    this.deleteSearchResultItem.emit(this.item);
  }

  onMoreActionsClick(event: Event, actionMenu: TieredMenu): void {
    event.stopPropagation();
    actionMenu.toggle(event);
  }

  onMenuItemClick(event: Event, item: BDMenuItem): void {
    if (item.disabled) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  private isRowInteractionBlocked(): boolean {
    return this.item.deleted || this.item.disabled || this.actionDisabled;
  }

  private isContentDetailsInteractionBlocked(): boolean {
    return this.item.deleted === true || this.item.disabled === true;
  }

  private isFromNestedInteractiveTarget(event: Event): boolean {
    const target = event.target as HTMLElement | null;
    const currentTarget = event.currentTarget as HTMLElement | null;
    if (!target) {
      return false;
    }

    const closestInteractive = target.closest(
      'button, a, input, select, textarea, .p-button, .p-checkbox',
    );

    return !!closestInteractive && closestInteractive !== currentTarget;
  }
}
